import {
  SheetHeader,
  SheetTitle,
  usePreventSheetClose,
} from "@/components/ui/sheet";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { format, isSameDay } from "date-fns";
import {
  StipendExpense,
  StipendExpenseAutoApprovalMetadata,
  StipendWalletResponse,
  useSubmitStipendExpenseMutation,
  StipendRuleFrequency,
} from "@common/utils/graphql";
import { SheetBackButton } from "@common/components/SheetActions/SheetBackButton";
import { CreateStipendForm } from "./CreateStipendForm";
import { StipendFormSchema, StipendFormValues } from "../forms/Forms";
import { ToastContent, useToast } from "@/components/ui/use-toast";
import { hasDirtyFields, receiptInputData } from "@/utils/utils";
import { formatDollars } from "@common/components/utils";
import { useRef, useState } from "react";
import { SelectFromHypercardContent } from "@domains/reports/pages/sheets/SelectFromHypercardContent";
import {
  SheetTabsContainer,
  SheetTabsContent,
  SheetTabsList,
} from "@common/components";

interface Props {
  stipendWallet?: StipendWalletResponse;
  onSubmitted?: () => void;
  expense?: StipendExpense;
  onCloseView?: () => void;
}

const SheetTabs = [
  {
    name: "Manual",
    value: "manual",
  },
  {
    name: "Select from Hypercard",
    value: "select-from-hypercard",
  },
];

export const CreateStipendSheetContent = ({
  stipendWallet,
  onSubmitted,
  expense,
  onCloseView,
}: Props) => {
  const viewOnly = !!expense;
  const { toast } = useToast();
  const [tab, setTab] = useState("manual");
  const didPrefillWithOcr = useRef(false);
  const maxAmount = stipendWallet?.remainingBalance ?? 1; // stipendWallet is only undefined when this is view only
  const isDailyStipend =
    stipendWallet?.stipendDetail?.frequency === StipendRuleFrequency.Daily;
  const formSchema = z.object({
    merchant: z.string().min(1, { message: "Merchant is required" }),
    amount: z.coerce
      .number({ message: "Amount is required" })
      .min(0.01, { message: "Amount must be greater than $0.00" })
      .max(maxAmount, {
        message: `Amount cannot exceed ${formatDollars(maxAmount)}`,
      }),
    date: z
      .date({
        required_error: "A transaction date is required",
      })
      .refine(
        (date: Date) =>
          isDailyStipend ? isSameDay(new Date(), date) : new Date() >= date,
        isDailyStipend ? "Date must be today" : "Date cannot be in the future",
      ),
    receipt: z.any().default(null),
    notes: z.string().optional(),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: isDailyStipend ? new Date() : undefined,
    },
  });

  usePreventSheetClose(() => {
    return hasDirtyFields(form) || didPrefillWithOcr.current;
  });

  const handlePrefillForm = (formValues: Partial<StipendFormValues>) => {
    didPrefillWithOcr.current = true;
    const amount =
      formValues.amount && formValues.amount > maxAmount
        ? maxAmount
        : formValues.amount;
    form.reset({ ...formValues, amount });
    setTab("manual");
  };

  const getSubmittedToastMessage = (
    approvalMetadata?: StipendExpenseAutoApprovalMetadata | null,
  ): ToastContent => {
    if (approvalMetadata?.isAutoApproved) {
      return {
        title: "Stipend Auto-Approved!",
        variant: "default",
        reason: approvalMetadata.reason || undefined,
      };
    }
    const toastContent: ToastContent = {
      title: "Stipend claim submitted successfully",
      reason: approvalMetadata?.reason || undefined,
      variant: "warning",
    };
    return toastContent;
  };

  const onSubmit = async (v: z.infer<typeof StipendFormSchema>) => {
    const [error, receiptFileData] = await receiptInputData(v.receipt);
    if (error) return;

    try {
      const { data } = await submitExpense({
        variables: {
          stipendWalletId: stipendWallet!.id,
          transactionDate: format(v.date, "yyyy-MM-dd"),
          amount: v.amount,
          merchantName: v.merchant,
          notes: v.notes,
          receiptFileData,
        },
      });
      if (
        data?.submitStipendExpenseV2.__typename !==
        "MutationSubmitStipendExpenseV2Success"
      ) {
        throw new Error();
      }

      // Check if submitted
      if (
        data.submitStipendExpenseV2.data.__typename ===
        "SubmittedStipendExpenseResult"
      ) {
        const autoApprovalMetadata =
          data.submitStipendExpenseV2.data.autoApprovalMetadata;

        const toastContent = getSubmittedToastMessage(autoApprovalMetadata);
        toast(toastContent);

        onSubmitted?.();
      } else {
        // TODO: Aaron you gotta handle policy checker case here!
        throw new Error();
      }
    } catch (e) {
      toast({
        title: "Failed to submit stipend claim, please try again",
        variant: "destructive",
      });
    }
  };

  const [submitExpense, { loading: isSubmitting }] =
    useSubmitStipendExpenseMutation();

  return (
    <>
      {onCloseView && <SheetBackButton onClick={onCloseView} />}
      <SheetHeader>
        <SheetTitle>{viewOnly ? "View Claim" : "Submit Claim"}</SheetTitle>
      </SheetHeader>
      <SheetTabsContainer value={tab} onValueChange={setTab}>
        {!viewOnly && (
          <SheetTabsList tabs={SheetTabs} disabled={isSubmitting} />
        )}
        <SheetTabsContent value="manual">
          <CreateStipendForm
            form={form}
            onSubmit={onSubmit}
            viewOnly={viewOnly}
            expense={expense!}
            isSubmitting={isSubmitting}
            onParseReceipt={handlePrefillForm}
          />
        </SheetTabsContent>
        <SheetTabsContent value="select-from-hypercard">
          <SelectFromHypercardContent onSelection={handlePrefillForm} />
        </SheetTabsContent>
      </SheetTabsContainer>
    </>
  );
};
